import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
    Box,
    Card,
    Typography,
    Button,
    Divider,
    Stack,
    Chip,
    Grid,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";

import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";

import { getCategories, getProducts } from "../../utils/storage";

import CartToast from "../components/CartToast";

/* ---------- PRODUCT DATA ----------
   Groups are the catalogue's categories, so /product-group/Refrigerant%20Gas
   lists everything filed under that category. Nothing is hard-coded here.     */

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const loadProducts = () => {
    const categoryName = Object.fromEntries(
        getCategories().map((c) => [c.id, c.name])
    );

    return getProducts()
        .filter((p) => p.isActive !== false)
        .map((p) => ({
            id: p.id,
            title: p.name,
            group: categoryName[p.categoryId] || "",
            brand: p.brand,
            price: inr(p.salePrice),
            oldPrice: p.mrp > p.salePrice ? inr(p.mrp) : "",
            stock: p.availability || "In stock",
            images: p.images || [],
        }));
};

export default function ProductGroup() {
    const { product } = useParams();
    const navigate = useNavigate();

        useEffect(() => {

        document.title =
            "Product Group | Tecniqa";

    }, []);

    const [products, setProducts] = useState([]);

    useEffect(() => {
        setProducts(loadProducts());
    }, []);

    const filteredProducts = useMemo(() => {
        if (!product) return products;
        const wanted = decodeURIComponent(product).toLowerCase();
        return products.filter((p) => p.group.toLowerCase() === wanted);
    }, [product, products]);

    /* ---------- IMAGE STATE ---------- */
    const [selectedImages, setSelectedImages] = useState({});

    const handleImageChange = (productId, image) => {
        setSelectedImages((prev) => ({ ...prev, [productId]: image }));
    };

    /* ---------- TOAST STATE ---------- */
    const [toast, setToast] = useState({
        visible: false,
        message: "",
        type: "success",
    });

    const showToast = (message, type = "success") => {
        setToast({ visible: true, message, type });
        setTimeout(
            () => setToast((t) => ({ ...t, visible: false })),
            2800
        );
    };

    /* ---------- ADD TO CART ---------- */
    const addToCart = (prod, selectedImage) => {
        const cart = JSON.parse(localStorage.getItem("cart")) || [];

        const existing = cart.find((item) => item.title === prod.title);

        const numericPrice = Number(
            prod.price.replace(/[₹Rs.,\s]/g, "")
        );

        if (existing) {

            return true; // duplicate
        } else {
            cart.push({
                title: prod.title,
                image: selectedImage,
                price: numericPrice,
                qty: 1,
            });
            localStorage.setItem("cart", JSON.stringify(cart));
            window.dispatchEvent(new Event("cartUpdated"));
            return false;
        }
    };

    return (
        <Box
            sx={{
                background:
                    "linear-gradient(to bottom, #f6f7f8, #eef1f3)",
                minHeight: "100vh",
                pt: { xs: 3,md: 4 },
                pb: 5,
                px: { xs: 2, sm: 3, md: 4 },
            }}
        >
            <Box sx={{ maxWidth: 1320, mx: "auto" }}>

                {/* HEADER */}
                {/* BACK BUTTON */}
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/")}
                    sx={{
                        textTransform: "none",
                        borderRadius: "12px",
                        fontWeight: 600,
                        mb: 2.5,
                        color: "#1d2939",
                        borderColor: "#d0d5dd",
                        px: 2.2,
                        py: 0.8,
                        fontSize: "13px",
                        backgroundColor: "#fff",
                        "&:hover": {
                            borderColor: "#98a2b3",
                            backgroundColor: "#f8fafc",
                        },
                    }}
                >
                    Back
                </Button>



                {/* PRODUCT LIST */}
                <Stack spacing={4}>
                    {filteredProducts.map((prod) => {
                        const selectedImage =
                            selectedImages[prod.id] || prod.images[0];

                        return (
                            <Card
                                key={prod.id}
                                sx={{
                                    p: { xs: 2, md: 3 },
                                    borderRadius: "28px",
                                    background: "rgba(255,255,255,0.95)",
                                    border: "1px solid rgba(0,0,0,0.06)",
                                    boxShadow:
                                        "0 10px 35px rgba(15,23,42,0.06)",
                                }}
                            >
                                {/* TOP SECTION */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 4,
                                        alignItems: "flex-start",
                                        flexDirection: {
                                            xs: "column",
                                            md: "row",
                                        },
                                    }}
                                >
                                    {/* LEFT IMAGE SECTION */}
                                    <Box
                                        sx={{
                                            width: {
                                                xs: "100%",
                                                md: "42%",
                                            },
                                            display: "flex",
                                            gap: 1.5,
                                        }}
                                    >
                                        {/* THUMBNAILS */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                            }}
                                        >
                                            {prod.images.map((img, index) => (
                                                <Box
                                                    key={index}
                                                    onMouseEnter={() =>
                                                        handleImageChange(
                                                            prod.id,
                                                            img
                                                        )
                                                    }
                                                    sx={{
                                                        width: 62,
                                                        height: 62,
                                                        border:
                                                            selectedImage ===
                                                                img
                                                                ? "2px solid #111827"
                                                                : "1px solid #dcdfe4",
                                                        borderRadius: "14px",
                                                        overflow: "hidden",
                                                        cursor: "pointer",
                                                        backgroundColor:
                                                            "#fff",
                                                        p: 0.7,
                                                        transition: "0.3s",
                                                        "&:hover": {
                                                            borderColor:
                                                                "#111827",
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={img}
                                                        alt=""
                                                        sx={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit:
                                                                "contain",
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>

                                        {/* MAIN IMAGE */}
                                        <Box
                                            sx={{
                                                flex: 1,
                                                border: "1px solid rgba(0,0,0,0.06)",
                                                borderRadius: "24px",
                                                background:
                                                    "linear-gradient(145deg,#ffffff,#f8fafc)",
                                                p: 2,
                                                minHeight: 360,
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <InnerImageZoom
                                                src={selectedImage}
                                                zoomSrc={selectedImage}
                                                zoomType="hover"
                                                zoomScale={1.5}
                                                hideHint={true}
                                                width={340}
                                                height={340}
                                                imgAttributes={{
                                                    style: {
                                                        objectFit: "contain",
                                                        width: "100%",
                                                        height: "100%",
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* RIGHT DETAILS */}
                                    <Box sx={{ flex: 1 }}>
                                        {/* TITLE */}
                                        <Typography
                                            variant="h4"
                                            fontWeight={800}
                                            sx={{
                                                lineHeight: 1.3,
                                                color: "#111827",
                                                fontSize: {
                                                    xs: "28px",
                                                    md: "38px",
                                                },
                                            }}
                                        >
                                            {prod.title}
                                        </Typography>

                                        {/* BRAND + STOCK */}
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            alignItems="center"
                                            sx={{
                                                mt: 1.5,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: "#6b7280",
                                                    fontSize: "14px",
                                                }}
                                            >
                                                Brand :
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        ml: 1,
                                                        color: "#111827",
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {prod.brand}
                                                </Box>
                                            </Typography>

                                            <Chip
                                                label={prod.stock}
                                                size="small"
                                                sx={{
                                                    backgroundColor:
                                                        "#ecfdf3",
                                                    color: "#166534",
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </Stack>

                                        {/* PRICE */}
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            alignItems="center"
                                            sx={{ mt: 2, flexWrap: "wrap" }}
                                        >
                                            <Typography
                                                variant="h4"
                                                fontWeight={800}
                                                sx={{
                                                    color: "#111827",
                                                    fontSize: {
                                                        xs: "30px",
                                                        md: "42px",
                                                    },
                                                }}
                                            >
                                                {prod.price}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    textDecoration:
                                                        "line-through",
                                                    color: "#9ca3af",
                                                    fontSize: "18px",
                                                }}
                                            >
                                                {prod.oldPrice}
                                            </Typography>

                                            <Chip
                                                label="20% OFF"
                                                sx={{
                                                    background: "#dbeafe",
                                                    color: "#1d4ed8",
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </Stack>

                                        {/* DESCRIPTION */}
                                        <Typography
                                            sx={{
                                                mt: 2.5,
                                                color: "#4b5563",
                                                lineHeight: 1.9,
                                                fontSize: "14px",
                                            }}
                                        >
                                            Professional HVAC servicing
                                            equipment engineered for precision,
                                            durability and industrial-grade
                                            performance for refrigeration and
                                            air-conditioning applications.
                                        </Typography>

                                        {/* HIGHLIGHTS */}
                                        <Box
                                            sx={{
                                                mt: 3,
                                                background:
                                                    "linear-gradient(135deg,#f8fafc,#f1f5f9)",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "20px",
                                                p: 2,
                                            }}
                                        >
                                            <Typography
                                                fontWeight={700}
                                                sx={{
                                                    color: "#111827",
                                                    mb: 1.5,
                                                    fontSize: "16px",
                                                }}
                                            >
                                                Highlights
                                            </Typography>

                                            <Grid container spacing={1}>
                                                {[
                                                    "Heavy-duty build",
                                                    "High precision",
                                                    "Portable design",
                                                    "Energy efficient",
                                                ].map((item, index) => (
                                                    <Grid
                                                        item
                                                        xs={6}
                                                        key={index}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    "13px",
                                                                color:
                                                                    "#4b5563",
                                                            }}
                                                        >
                                                            • {item}
                                                        </Typography>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>

                                        {/* BUTTONS */}
                                        <Stack
                                            direction={{
                                                xs: "column",
                                                sm: "row",
                                            }}
                                            spacing={2}
                                            sx={{ mt: 4 }}
                                        >
                                            <Button
                                                variant="contained"
                                                startIcon={
                                                    <ShoppingCartOutlinedIcon />
                                                }
                                                sx={{
                                                    background:
                                                        "linear-gradient(135deg,#111827,#1f2937)",
                                                    py: 1.2,
                                                    px: 3,
                                                    borderRadius: "14px",
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    fontSize: "14px",
                                                    boxShadow:
                                                        "0 10px 20px rgba(17,24,39,0.18)",
                                                    "&:hover": {
                                                        background:
                                                            "linear-gradient(135deg,#0f172a,#111827)",
                                                    },
                                                }}
                                                onClick={() => {
                                                    const isDuplicate =
                                                        addToCart(
                                                            prod,
                                                            selectedImage
                                                        );
                                                    showToast(
                                                        isDuplicate
                                                            ? "Item already been added to cart!"
                                                            : "Added to cart successfully",
                                                        isDuplicate
                                                            ? "warning"
                                                            : "success"
                                                    );
                                                }}
                                            >
                                                Add to Cart
                                            </Button>

                                            <Button
                                                variant="contained"
                                                startIcon={
                                                    <FlashOnOutlinedIcon />
                                                }
                                                sx={{
                                                    background:
                                                        "linear-gradient(135deg,#d4af37,#f4d03f)",
                                                    py: 1.2,
                                                    px: 3,
                                                    borderRadius: "14px",
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    fontSize: "14px",
                                                    color: "#111827",
                                                    boxShadow:
                                                        "0 10px 20px rgba(212,175,55,0.25)",
                                                    "&:hover": {
                                                        background:
                                                            "linear-gradient(135deg,#c49b1e,#e6c229)",
                                                    },
                                                }}
                                                onClick={() => {
                                                    addToCart(
                                                        prod,
                                                        selectedImage
                                                    );
                                                    navigate("/checkout");
                                                }}
                                            >
                                                Buy Now
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Box>

                                {/* DESCRIPTION SECTION */}
                                <Divider
                                    sx={{
                                        my: 4,
                                        borderColor: "rgba(0,0,0,0.08)",
                                    }}
                                />

                                <Grid container spacing={3}>
                                    {/* DESCRIPTION */}
                                    <Grid item xs={12} md={7}>
                                        <Typography
                                            variant="h6"
                                            fontWeight={700}
                                            sx={{
                                                color: "#111827",
                                                mb: 1.5,
                                            }}
                                        >
                                            Product Description
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: "#4b5563",
                                                lineHeight: 1.9,
                                                fontSize: "14px",
                                            }}
                                        >
                                            {prod.title} is manufactured using
                                            advanced HVAC servicing technology
                                            to ensure superior efficiency,
                                            precision and reliability for
                                            industrial and commercial
                                            refrigeration environments.
                                        </Typography>
                                    </Grid>

                                    {/* SPECIFICATIONS */}
                                    <Grid item xs={12} md={5}>
                                        <Typography
                                            variant="h6"
                                            fontWeight={700}
                                            sx={{
                                                color: "#111827",
                                                mb: 1.5,
                                            }}
                                        >
                                            Specifications
                                        </Typography>

                                        <Grid container spacing={1.5}>
                                            {[
                                                {
                                                    label: "Brand",
                                                    value: prod.brand,
                                                },
                                                {
                                                    label: "Category",
                                                    value: prod.group,
                                                },
                                                {
                                                    label: "Availability",
                                                    value: prod.stock,
                                                },
                                                {
                                                    label: "Warranty",
                                                    value: "1 Year",
                                                },
                                            ].map((item, index) => (
                                                <Grid
                                                    item
                                                    xs={6}
                                                    key={index}
                                                >
                                                    <Box
                                                        sx={{
                                                            background:
                                                                "#f8fafc",
                                                            border:
                                                                "1px solid #e5e7eb",
                                                            borderRadius:
                                                                "16px",
                                                            p: 1.8,
                                                            height: "100%",
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 700,
                                                                color:
                                                                    "#111827",
                                                                fontSize:
                                                                    "13px",
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            {item.label}
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                color:
                                                                    "#6b7280",
                                                                fontSize:
                                                                    "13px",
                                                            }}
                                                        >
                                                            {item.value}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Card>
                        );
                    })}
                </Stack>
            </Box>

            {/* CART TOAST */}
            <CartToast
                visible={toast.visible}
                message={toast.message}
                type={toast.type}
            />
        </Box>
    );
}